import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TestTube, FlaskConical, RefreshCw, Plus, X, Search,
  ChevronDown, ChevronRight, Printer, CheckCircle2, AlertTriangle,
  Loader2, ArrowLeft,
} from 'lucide-react';
import { format } from 'date-fns';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { clsx } from 'clsx';

// ── Types ──────────────────────────────────────────────────────────
interface TestMaster {
  id: string; loinc_code: string; test_name: string; category: string;
  unit: string; ref_range_male: string; ref_range_female: string;
  critical_low: number | null; critical_high: number | null;
}
interface LabOrder {
  id: string; barcode: string; order_date: string; status: string;
  tests: TestWithRef[]; results: ResultRow[] | null;
  patient_uid: string; patient_name_enc: string; gender: string; dob: string;
  ordered_by_name: string; ordered_by_spec: string;
  reported_by_name: string | null; reported_at: string | null;
  critical_values: ResultRow[] | null; notes: string | null;
}
interface TestWithRef {
  loinc_code: string; test_name: string; category: string;
  unit?: string; ref_range_male?: string; ref_range_female?: string;
  critical_low?: number; critical_high?: number;
}
interface ResultRow {
  loinc_code: string; test_name: string; value: string;
  unit?: string; ref_range?: string; flag: string;
}

// ── Helpers ────────────────────────────────────────────────────────
const STATUS_STYLE: Record<string, string> = {
  ordered:          'bg-gray-100 text-gray-600',
  sample_collected: 'bg-blue-100 text-blue-700',
  processing:       'bg-orange-100 text-orange-600',
  resulted:         'bg-purple-100 text-purple-700',
  reported:         'bg-green-100 text-green-700',
  cancelled:        'bg-red-100 text-red-500',
};

const FLAG_STYLE: Record<string, string> = {
  normal:        'text-gray-700',
  low:           'text-yellow-700 font-semibold',
  high:          'text-orange-600 font-semibold',
  critical_low:  'text-red-600 font-bold',
  critical_high: 'text-red-600 font-bold',
};

function calcAge(dob: string): number {
  if (!dob) return 0;
  return Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
}

function autoFlag(value: string, test: TestWithRef, gender: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return 'normal';
  if (test.critical_low  != null && num <= test.critical_low)  return 'critical_low';
  if (test.critical_high != null && num >= test.critical_high) return 'critical_high';
  const range = (gender === 'female' ? test.ref_range_female : test.ref_range_male) ?? '';
  if (!range) return 'normal';
  // Strip trailing % for percentage ranges (e.g. "<5.7%")
  const clean = range.replace(/%$/, '').trim();
  // Handle <X (upper-bound only)
  const ltm = clean.match(/^<\s*([\d.]+)$/);
  if (ltm) { return num > parseFloat(ltm[1]) ? 'high' : 'normal'; }
  // Handle >X (lower-bound only)
  const gtm = clean.match(/^>\s*([\d.]+)$/);
  if (gtm) { return num < parseFloat(gtm[1]) ? 'low' : 'normal'; }
  // Handle X-Y range
  const rm = clean.match(/^([\d.]+)\s*[-–]\s*([\d.]+)$/);
  if (rm) {
    const lo = parseFloat(rm[1]), hi = parseFloat(rm[2]);
    if (num < lo) return 'low';
    if (num > hi) return 'high';
  }
  return 'normal';
}

// ── New Direct Order Modal ─────────────────────────────────────────
function DirectOrderModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [step,        setStep]        = useState<'patient' | 'tests'>('patient');
  const [patientQ,    setPatientQ]    = useState('');
  const [patient,     setPatient]     = useState<any>(null);
  const [testSearch,  setTestSearch]  = useState('');
  const [selected,    setSelected]    = useState<Record<string, TestMaster>>({});
  const [urgency,     setUrgency]     = useState<'routine'|'urgent'|'stat'>('routine');
  const [notes,       setNotes]       = useState('');

  const { data: pData, isFetching: pLoading } = useQuery({
    queryKey: ['lab-pt-search', patientQ],
    queryFn:  () => patientQ.length >= 2
      ? api.get('/lab/patients', { params: { search: patientQ } }).then(r => r.data)
      : Promise.resolve({ patients: [] }),
    enabled: patientQ.length >= 2,
  });

  const { data: testData } = useQuery({
    queryKey: ['lab-tests', testSearch],
    queryFn:  () => api.get('/lab/tests', {
      params: { q: testSearch || undefined }
    }).then(r => r.data),
  });

  const mut = useMutation({
    mutationFn: () => api.post('/lab/orders', {
      patient_id: patient.id,
      tests: Object.values(selected).map(t => ({
        loinc_code: t.loinc_code, test_name: t.test_name, category: t.category,
      })),
      urgency, notes: notes || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lab-orders'] });
      qc.invalidateQueries({ queryKey: ['lab-stats'] });
      onClose();
    },
  });

  const patients: any[] = pData?.patients ?? [];
  const tests: TestMaster[] = Array.isArray(testData) ? testData : [];
  const grouped = tests.reduce((acc: Record<string, TestMaster[]>, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});
  const selCount = Object.keys(selected).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {step === 'tests' && (
              <button onClick={() => setStep('patient')} className="text-gray-400 hover:text-navy">
                <ArrowLeft size={18}/>
              </button>
            )}
            <h2 className="font-bold text-navy text-lg">
              {step === 'patient' ? 'New Direct Lab Order' : `Select Tests — ${patient?.patient_uid}`}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-navy"><X size={20}/></button>
        </div>

        {step === 'patient' ? (
          <div className="p-5 flex flex-col gap-4">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
              <input
                type="text"
                value={patientQ}
                onChange={e => setPatientQ(e.target.value)}
                placeholder="Search patient by name or UID…"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm
                           focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange"
                autoFocus
              />
              {pLoading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-orange"/>}
            </div>

            {patients.length > 0 && (
              <div className="border border-gray-100 rounded-xl overflow-hidden divide-y divide-gray-50">
                {patients.map((p: any) => (
                  <button key={p.id} onClick={() => { setPatient(p); setStep('tests'); }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange/5 text-left transition">
                    <div className="w-9 h-9 rounded-full bg-navy/10 flex items-center justify-center text-navy font-bold text-sm flex-shrink-0">
                      {(p.patient_uid ?? '?').slice(0,2).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-navy text-sm">{p.patient_uid}</p>
                      <p className="text-xs text-gray-400">{p.name ?? '—'}</p>
                    </div>
                    <ChevronRight size={14} className="ml-auto text-gray-300"/>
                  </button>
                ))}
              </div>
            )}
            {patientQ.length >= 2 && !pLoading && patients.length === 0 && (
              <p className="text-center text-sm text-gray-400 py-4">No patients found</p>
            )}
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-gray-100 flex items-center gap-3">
              <Search size={14} className="text-gray-400"/>
              <input
                type="text"
                value={testSearch}
                onChange={e => setTestSearch(e.target.value)}
                placeholder="Search tests…"
                className="flex-1 text-sm focus:outline-none"
                autoFocus
              />
              {selCount > 0 && (
                <span className="bg-orange text-navy text-xs font-bold px-2 py-0.5 rounded-full">
                  {selCount} selected
                </span>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {Object.entries(grouped).map(([cat, catTests]) => (
                <div key={cat}>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 capitalize">{cat}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {catTests.map(t => {
                      const chk = !!selected[t.loinc_code];
                      return (
                        <label key={t.loinc_code}
                          className={clsx(
                            'flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition text-sm',
                            chk ? 'border-orange bg-orange/5 text-navy font-medium'
                                : 'border-gray-100 hover:border-orange/40 text-gray-700'
                          )}>
                          <input type="checkbox" className="hidden"
                            checked={chk}
                            onChange={() => {
                              setSelected(prev => {
                                const next = { ...prev };
                                if (chk) delete next[t.loinc_code];
                                else next[t.loinc_code] = t;
                                return next;
                              });
                            }}
                          />
                          <div className={clsx('w-4 h-4 rounded flex items-center justify-center flex-shrink-0',
                            chk ? 'bg-orange' : 'border border-gray-300')}>
                            {chk && <CheckCircle2 size={12} className="text-navy"/>}
                          </div>
                          <span className="leading-tight">{t.test_name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-gray-100 space-y-3">
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-gray-400 mb-1">Urgency</label>
                  <select value={urgency} onChange={e => setUrgency(e.target.value as any)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange/30">
                    <option value="routine">Routine</option>
                    <option value="urgent">Urgent</option>
                    <option value="stat">STAT</option>
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-400 mb-1">Notes (optional)</label>
                  <input value={notes} onChange={e => setNotes(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange/30"
                    placeholder="Fasting sample…"
                  />
                </div>
              </div>
              {mut.isError && (
                <p className="text-red-600 text-sm">{(mut.error as any)?.response?.data?.error ?? 'Failed to create order'}</p>
              )}
              <button
                onClick={() => mut.mutate()}
                disabled={selCount === 0 || mut.isPending}
                className="w-full py-2.5 rounded-xl bg-orange text-navy font-bold text-sm
                           hover:bg-orange-dark disabled:opacity-40 flex items-center justify-center gap-2">
                {mut.isPending && <Loader2 size={14} className="animate-spin"/>}
                Create Order ({selCount} test{selCount !== 1 ? 's' : ''})
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Result Entry Modal ─────────────────────────────────────────────
function ResultEntryModal({ order, onClose }: { order: LabOrder; onClose: () => void }) {
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>(
    () => {
      const init: Record<string, string> = {};
      if (order.results) {
        order.results.forEach(r => { init[r.loinc_code] = r.value; });
      }
      return init;
    }
  );
  const [notes, setNotes] = useState(order.notes ?? '');

  const gender = order.gender ?? 'male';

  const rows = order.tests.map(t => {
    const val  = values[t.loinc_code] ?? '';
    const flag = autoFlag(val, t, gender);
    const ref  = gender === 'female' ? t.ref_range_female : t.ref_range_male;
    return { ...t, val, flag, ref };
  });

  const mut = useMutation({
    mutationFn: () => {
      const entered = rows.filter(r => r.val.trim() !== '');
      if (entered.length === 0) throw new Error('Enter at least one result value');
      return api.post(`/lab/orders/${order.id}/results`, {
        results: entered.map(r => ({
          loinc_code: r.loinc_code,
          test_name:  r.test_name,
          value:      r.val.trim(),
          ...(r.unit  != null ? { unit:      r.unit }      : {}),
          ...(r.ref   != null ? { ref_range: r.ref }       : {}),
          flag:       r.flag,
        })),
        ...(notes ? { notes } : {}),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lab-orders'] });
      qc.invalidateQueries({ queryKey: ['lab-stats'] });
      onClose();
    },
  });

  const hasCritical = rows.some(r => r.flag === 'critical_low' || r.flag === 'critical_high');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-navy text-lg">Enter Results</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {order.patient_uid} · {order.barcode}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-navy"><X size={20}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-400 uppercase">
                <th className="text-left pb-2">Test</th>
                <th className="text-left pb-2">Reference Range</th>
                <th className="text-left pb-2 w-36">Value</th>
                <th className="text-left pb-2">Unit</th>
                <th className="text-left pb-2">Flag</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map(r => (
                <tr key={r.loinc_code} className={clsx(
                  r.flag === 'critical_low' || r.flag === 'critical_high' ? 'bg-red-50' :
                  r.flag === 'low' || r.flag === 'high' ? 'bg-yellow-50/50' : ''
                )}>
                  <td className="py-2.5 pr-3 font-medium text-navy">{r.test_name}</td>
                  <td className="py-2.5 pr-3 text-xs text-gray-500">{r.ref ?? '—'}</td>
                  <td className="py-2.5 pr-3">
                    <input
                      type="text"
                      value={r.val}
                      onChange={e => setValues(prev => ({ ...prev, [r.loinc_code]: e.target.value }))}
                      className="w-full px-2 py-1 rounded-lg border border-gray-200 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange"
                      placeholder="—"
                    />
                  </td>
                  <td className="py-2.5 pr-3 text-xs text-gray-500">{r.unit ?? '—'}</td>
                  <td className={clsx('py-2.5 text-xs', FLAG_STYLE[r.flag] ?? 'text-gray-600')}>
                    {r.flag === 'normal' ? '—' : r.flag.replace('_', ' ').toUpperCase()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4">
            <label className="block text-xs text-gray-400 mb-1">Lab Notes (optional)</label>
            <textarea
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-none
                         focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange"
              placeholder="Haemolysed sample, repeat recommended…"
            />
          </div>

          {hasCritical && (
            <div className="mt-3 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertTriangle size={16} className="text-red-600 flex-shrink-0"/>
              <p className="text-sm text-red-700 font-medium">
                Critical values detected — order will be auto-finalized and flagged.
              </p>
            </div>
          )}
          {mut.isError && (
            <div className="mt-3 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertTriangle size={16} className="text-red-600 flex-shrink-0"/>
              <p className="text-sm text-red-700">
                {(mut.error as any)?.response?.data?.error ?? (mut.error as any)?.message ?? 'Failed to save results. Please try again.'}
              </p>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-gray-100 flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            className="flex-1 py-2.5 rounded-xl bg-orange text-navy font-bold text-sm
                       hover:bg-orange-dark disabled:opacity-40 flex items-center justify-center gap-2">
            {mut.isPending && <Loader2 size={14} className="animate-spin"/>}
            Save Results
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Lab Report Print (portal) ──────────────────────────────────────
function LabReportPrint({ order, hospitalName, onDone }: {
  order: LabOrder; hospitalName: string; onDone: () => void;
}) {
  const gender = order.gender ?? 'male';
  const age    = calcAge(order.dob);

  let portal = document.getElementById('as-print-portal');
  if (!portal) {
    portal = document.createElement('div');
    portal.id = 'as-print-portal';
    document.body.appendChild(portal);
  }

  return createPortal(
    <div className="print-only" style={{ fontFamily: 'Arial, sans-serif', fontSize: '11pt', color: '#000', padding: '12mm' }}>
      {/* Header */}
      <div style={{ borderBottom: '2px solid #1a1a2e', paddingBottom: '6mm', marginBottom: '5mm' }}>
        <h1 style={{ margin: 0, fontSize: '16pt', color: '#1a1a2e' }}>{hospitalName}</h1>
        <p style={{ margin: '2mm 0 0', fontSize: '10pt', color: '#555' }}>Laboratory Investigation Report</p>
      </div>

      {/* Patient info strip */}
      <table style={{ width: '100%', marginBottom: '5mm', fontSize: '10pt' }}>
        <tbody>
          <tr>
            <td><strong>Patient:</strong> {order.patient_uid}</td>
            <td><strong>Age / Gender:</strong> {age}Y / {order.gender ?? '—'}</td>
            <td><strong>Barcode:</strong> {order.barcode}</td>
          </tr>
          <tr>
            <td><strong>Ordered by:</strong> {order.ordered_by_name}</td>
            <td><strong>Date:</strong> {format(new Date(order.order_date), 'dd MMM yyyy, hh:mm a')}</td>
            <td><strong>Status:</strong> {order.status}</td>
          </tr>
        </tbody>
      </table>

      {/* Results table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt' }}>
        <thead>
          <tr style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>
            <th style={{ padding: '3mm', textAlign: 'left' }}>Test</th>
            <th style={{ padding: '3mm', textAlign: 'left' }}>Result</th>
            <th style={{ padding: '3mm', textAlign: 'left' }}>Unit</th>
            <th style={{ padding: '3mm', textAlign: 'left' }}>Reference Range</th>
            <th style={{ padding: '3mm', textAlign: 'left' }}>Flag</th>
          </tr>
        </thead>
        <tbody>
          {(order.tests ?? []).map((t, i) => {
            const res = (order.results ?? []).find(r => r.loinc_code === t.loinc_code);
            const flag = res ? res.flag : 'normal';
            const isAbnormal = flag !== 'normal';
            const ref = gender === 'female' ? t.ref_range_female : t.ref_range_male;
            return (
              <tr key={t.loinc_code} style={{ backgroundColor: i % 2 === 0 ? '#f9f9f9' : '#fff' }}>
                <td style={{ padding: '2.5mm', borderBottom: '0.5pt solid #ddd' }}>{t.test_name}</td>
                <td style={{ padding: '2.5mm', borderBottom: '0.5pt solid #ddd',
                             color: isAbnormal ? '#c00' : '#000',
                             fontWeight: isAbnormal ? 'bold' : 'normal' }}>
                  {res?.value ?? '—'}
                </td>
                <td style={{ padding: '2.5mm', borderBottom: '0.5pt solid #ddd' }}>{res?.unit ?? t.unit ?? '—'}</td>
                <td style={{ padding: '2.5mm', borderBottom: '0.5pt solid #ddd' }}>{ref ?? '—'}</td>
                <td style={{ padding: '2.5mm', borderBottom: '0.5pt solid #ddd',
                             color: isAbnormal ? '#c00' : '#666',
                             fontWeight: isAbnormal ? 'bold' : 'normal' }}>
                  {flag === 'normal' ? '' : flag.replace('_', ' ').toUpperCase()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {order.notes && (
        <p style={{ marginTop: '4mm', fontSize: '9pt', color: '#555' }}>
          <strong>Lab Notes:</strong> {order.notes}
        </p>
      )}

      {/* Signature */}
      <div style={{ marginTop: '10mm', borderTop: '1pt solid #ccc', paddingTop: '4mm', display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '50mm', borderBottom: '1pt solid #000', marginBottom: '1mm' }}/>
          <p style={{ margin: 0, fontSize: '9pt' }}>
            {order.reported_by_name ?? order.ordered_by_name}<br/>
            Lab Technician
          </p>
        </div>
      </div>

    </div>,
    portal
  );
}

// ── Order Detail Panel ─────────────────────────────────────────────
function OrderDetail({
  orderId, onClose, hospitalName,
}: { orderId: string; onClose: () => void; hospitalName: string }) {
  const qc = useQueryClient();
  const [showResult, setShowResult]  = useState(false);
  const [showPrint,  setShowPrint]   = useState(false);

  const { data: order, isLoading } = useQuery<LabOrder>({
    queryKey: ['lab-order-detail', orderId],
    queryFn:  () => api.get(`/lab/orders/${orderId}`).then(r => r.data),
  });

  const collectMut = useMutation({
    mutationFn: () => api.patch(`/lab/orders/${orderId}/collect`, {}),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['lab-order-detail', orderId] }),
  });

  const validateMut = useMutation({
    mutationFn: () => api.post(`/lab/orders/${orderId}/validate`, {}),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ['lab-order-detail', orderId] });
      qc.invalidateQueries({ queryKey: ['lab-orders'] });
      qc.invalidateQueries({ queryKey: ['lab-stats'] });
    },
  });

  const handlePrint = () => {
    setShowPrint(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => setShowPrint(false), 500);
    }, 400);
  };

  if (isLoading || !order) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 size={24} className="animate-spin text-orange"/>
      </div>
    );
  }

  const gender = order.gender ?? 'male';
  const age    = calcAge(order.dob);
  const canCollect  = order.status === 'ordered';
  const canEnter    = order.status === 'sample_collected' || order.status === 'processing';
  const canValidate = order.status === 'resulted';
  const isReported  = order.status === 'reported';

  return (
    <>
      <div className="p-5 border-b border-gray-100 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-blue-600 text-sm font-bold">{order.barcode}</span>
            <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', STATUS_STYLE[order.status])}>
              {order.status.replace('_', ' ')}
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {order.patient_uid} · {age}Y · {order.gender ?? '—'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(isReported || order.results) && (
            <button onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-navy/5 text-navy text-sm hover:bg-navy/10">
              <Printer size={14}/> Print
            </button>
          )}
          <button onClick={onClose} className="text-gray-400 hover:text-navy"><X size={18}/></button>
        </div>
      </div>

      <div className="p-5 space-y-4 overflow-y-auto flex-1">
        {/* Tests ordered */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Tests Ordered</p>
          <div className="space-y-1">
            {order.tests.map(t => (
              <div key={t.loinc_code} className="flex items-center justify-between text-sm">
                <span className="text-gray-700">{t.test_name}</span>
                <span className="text-xs text-gray-400 capitalize">{t.category}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Results */}
        {order.results && order.results.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Results</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 uppercase">
                  <th className="text-left pb-1">Test</th>
                  <th className="text-left pb-1">Value</th>
                  <th className="text-left pb-1">Unit</th>
                  <th className="text-left pb-1">Ref Range</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {order.results.map(r => {
                  const t = order.tests.find(x => x.loinc_code === r.loinc_code);
                  const ref = t ? (gender === 'female' ? t.ref_range_female : t.ref_range_male) : r.ref_range;
                  return (
                    <tr key={r.loinc_code}>
                      <td className="py-1.5 pr-2 text-gray-700">{r.test_name}</td>
                      <td className={clsx('py-1.5 pr-2 font-semibold', FLAG_STYLE[r.flag] ?? 'text-gray-700')}>
                        {r.value}
                        {r.flag !== 'normal' && (
                          <span className="ml-1 text-xs">
                            {r.flag === 'critical_low' || r.flag === 'critical_high' ? 'CRITICAL' :
                             r.flag === 'low' ? 'LOW' : 'HIGH'}
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 pr-2 text-xs text-gray-500">{r.unit ?? '—'}</td>
                      <td className="py-1.5 text-xs text-gray-400">{ref ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {order.notes && (
          <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-600">
            <strong>Notes:</strong> {order.notes}
          </div>
        )}

        {/* Action buttons */}
        <div className="pt-2 space-y-2">
          {canCollect && (
            <button
              onClick={() => collectMut.mutate()}
              disabled={collectMut.isPending}
              className="w-full py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm
                         hover:bg-blue-700 disabled:opacity-40 flex items-center justify-center gap-2">
              {collectMut.isPending && <Loader2 size={14} className="animate-spin"/>}
              Mark Sample Collected
            </button>
          )}
          {canEnter && (
            <button
              onClick={() => setShowResult(true)}
              className="w-full py-2.5 rounded-xl bg-orange text-navy font-bold text-sm hover:bg-orange-dark">
              Enter Results
            </button>
          )}
          {canValidate && (
            <button
              onClick={() => setShowResult(true)}
              className="w-full py-2.5 rounded-xl bg-orange text-navy font-bold text-sm hover:bg-orange-dark">
              Update / Finalize Results
            </button>
          )}
          {canValidate && !showResult && (
            <button
              onClick={() => validateMut.mutate()}
              disabled={validateMut.isPending}
              className="w-full py-2.5 rounded-xl border border-green-500 text-green-700 font-semibold text-sm
                         hover:bg-green-50 disabled:opacity-40 flex items-center justify-center gap-2">
              {validateMut.isPending && <Loader2 size={14} className="animate-spin"/>}
              Validate & Finalize Report
            </button>
          )}
          {isReported && (
            <div className="flex items-center justify-center gap-2 py-2 text-green-600 text-sm font-medium">
              <CheckCircle2 size={16}/> Report finalized
            </div>
          )}
        </div>
      </div>

      {showResult && (
        <ResultEntryModal
          order={order}
          onClose={() => {
            setShowResult(false);
            qc.invalidateQueries({ queryKey: ['lab-order-detail', orderId] });
          }}
        />
      )}

      {showPrint && (
        <LabReportPrint order={order} hospitalName={hospitalName} onDone={() => setShowPrint(false)}/>
      )}
    </>
  );
}

// ── Main Page ──────────────────────────────────────────────────────
export default function LabPage() {
  const user         = useAuthStore(s => s.user);
  const queryClient  = useQueryClient();
  const today        = format(new Date(), 'yyyy-MM-dd');
  const [statusFilter, setStatus]    = useState('');
  const [selectedId,   setSelected]  = useState<string | null>(null);
  const [showNewOrder, setNewOrder]  = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['lab-orders', statusFilter, today],
    queryFn:  () => api.get('/lab/orders', {
      params: { status: statusFilter || undefined, date: today }
    }).then(r => r.data),
    refetchInterval: 30_000,
  });

  const { data: stats } = useQuery({
    queryKey: ['lab-stats', today],
    queryFn:  () => api.get('/lab/stats', { params: { date: today } }).then(r => r.data),
  });

  const orders: any[] = data?.orders ?? [];
  const hospitalName  = user?.hospitalName ?? 'Hospital';

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h1 className="text-xl font-bold text-navy">Laboratory (LIS)</h1>
          <p className="text-sm text-gray-400 mt-0.5">{today}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
            <RefreshCw size={13} className={isLoading ? 'animate-spin' : ''}/>
            Refresh
          </button>
          <button onClick={() => setNewOrder(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-orange text-navy font-semibold text-sm hover:bg-orange-dark">
            <Plus size={14}/> New Direct Order
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-5 gap-3 px-6 py-3">
          {[
            { label: 'Ordered',             val: stats.ordered_today,       color: 'border-gray-200 bg-gray-50' },
            { label: 'Pending Collection',  val: stats.pending_collection,  color: 'border-blue-200 bg-blue-50' },
            { label: 'In Lab',              val: stats.in_lab,              color: 'border-orange-200 bg-orange-50' },
            { label: 'Awaiting Validation', val: stats.awaiting_validation, color: 'border-purple-200 bg-purple-50' },
            { label: 'Reported',            val: stats.reported_today,      color: 'border-green-200 bg-green-50' },
          ].map(s => (
            <div key={s.label} className={clsx('border rounded-xl p-3', s.color)}>
              <div className="text-xl font-bold text-navy">{s.val ?? 0}</div>
              <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Status filter */}
      <div className="flex gap-2 px-6 pb-3 flex-wrap">
        {['', 'ordered', 'sample_collected', 'resulted', 'reported'].map(s => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={clsx('px-3 py-1.5 rounded-lg text-sm border transition',
              statusFilter === s
                ? 'bg-navy text-white border-navy'
                : 'bg-white text-gray-600 border-gray-200 hover:border-navy'
            )}>
            {s === '' ? 'All' : s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </button>
        ))}
      </div>

      {/* Content split: worklist + detail panel */}
      <div className="flex-1 flex overflow-hidden px-6 pb-6 gap-4">
        {/* Worklist */}
        <div className={clsx('flex-1 bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col',
          selectedId ? 'max-w-[calc(100%-380px)]' : 'max-w-full')}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                {['Barcode', 'Patient', 'Tests', 'Status', 'Ordered By', 'Time', 'Action'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {isLoading ? (
                <tr><td colSpan={7} className="text-center py-12">
                  <Loader2 size={22} className="mx-auto animate-spin text-orange mb-2"/>
                  <p className="text-sm text-gray-400">Loading orders…</p>
                </td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-16">
                  <FlaskConical size={36} className="mx-auto text-gray-300 mb-3"/>
                  <p className="text-gray-400 text-sm">No orders found for today</p>
                  <button onClick={() => setNewOrder(true)}
                    className="mt-3 px-4 py-2 bg-orange text-navy rounded-xl text-sm font-semibold hover:bg-orange-dark">
                    Create First Order
                  </button>
                </td></tr>
              ) : orders.map((order: any) => (
                <tr
                  key={order.id}
                  onClick={() => setSelected(order.id === selectedId ? null : order.id)}
                  className={clsx('hover:bg-gray-50 cursor-pointer transition-colors',
                    order.id === selectedId && 'bg-orange/5 border-l-2 border-orange'
                  )}>
                  <td className="px-4 py-3 font-mono text-xs text-blue-600">{order.barcode}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800 text-xs">{order.patient_uid}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 max-w-[160px] truncate">
                    {(order.tests ?? []).map((t: any) => t.test_name).slice(0, 2).join(', ')}
                    {(order.tests ?? []).length > 2 && ` +${order.tests.length - 2}`}
                  </td>
                  <td className="px-4 py-3">
                    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', STATUS_STYLE[order.status])}>
                      {order.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{order.ordered_by_name}</td>
                  <td className="px-4 py-3 text-xs text-gray-400">
                    {new Date(order.order_date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-4 py-3">
                    {order.status === 'ordered' && (
                      <span className="text-xs text-blue-600 font-medium">Pending sample</span>
                    )}
                    {order.status === 'sample_collected' && (
                      <span className="text-xs text-orange font-medium">Enter results →</span>
                    )}
                    {order.status === 'resulted' && (
                      <span className="text-xs text-purple-600 font-medium">Validate →</span>
                    )}
                    {order.status === 'reported' && (
                      <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle2 size={12}/> Done
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Detail panel */}
        {selectedId && (
          <div className="w-[360px] flex-shrink-0 bg-white border border-gray-200 rounded-xl flex flex-col overflow-hidden">
            <OrderDetail
              orderId={selectedId}
              onClose={() => setSelected(null)}
              hospitalName={hospitalName}
            />
          </div>
        )}
      </div>

      {showNewOrder && <DirectOrderModal onClose={() => setNewOrder(false)}/>}
    </div>
  );
}
